import { lazy } from "react";
import type { WorkspaceId } from "./WorkspaceDock";

type MemoryModule = typeof import("./MemoryWorkspace");
type CronModule = typeof import("./CronWorkspace");
type FileModule = typeof import("./FileWorkspace");
type ManagementModule = typeof import("./ManagementWorkspace");

let memoryPromise: Promise<MemoryModule> | null = null;
let cronPromise: Promise<CronModule> | null = null;
let filePromise: Promise<FileModule> | null = null;
let managementPromise: Promise<ManagementModule> | null = null;

function retryable<T>(
  current: Promise<T> | null,
  assign: (next: Promise<T> | null) => void,
  importer: () => Promise<T>,
) {
  if (current) return current;
  const pending = importer().catch((error: unknown) => {
    // 网络瞬断或资源更新不应永久污染模块缓存，下一次点击仍可重新加载。
    assign(null);
    throw error;
  });
  assign(pending);
  return pending;
}

export function preloadMemoryWorkspace() {
  return retryable(
    memoryPromise,
    (next) => (memoryPromise = next),
    () => import("./MemoryWorkspace"),
  );
}

export function preloadCronWorkspace() {
  return retryable(
    cronPromise,
    (next) => (cronPromise = next),
    () => import("./CronWorkspace"),
  );
}

export function preloadFileWorkspace() {
  return retryable(
    filePromise,
    (next) => (filePromise = next),
    () => import("./FileWorkspace"),
  );
}

export function preloadManagementWorkspace() {
  return retryable(
    managementPromise,
    (next) => (managementPromise = next),
    () => import("./ManagementWorkspace"),
  );
}

export const MemoryWorkspace = lazy(async () => ({
  default: (await preloadMemoryWorkspace()).MemoryWorkspace,
}));
export const CronWorkspace = lazy(async () => ({
  default: (await preloadCronWorkspace()).CronWorkspace,
}));
export const FileWorkspace = lazy(async () => ({
  default: (await preloadFileWorkspace()).FileWorkspace,
}));
export const ManagementWorkspace = lazy(async () => ({
  default: (await preloadManagementWorkspace()).ManagementWorkspace,
}));

/** 会话本身属于首屏代码，其余一级页面在出现明确操作意图时静默预取。 */
export function preloadWorkspace(workspace: WorkspaceId): Promise<unknown> | null {
  if (workspace === "memory") return preloadMemoryWorkspace();
  if (workspace === "cron") return preloadCronWorkspace();
  if (workspace === "files") return preloadFileWorkspace();
  if (workspace === "overview" || workspace === "more") return preloadManagementWorkspace();
  return null;
}
